import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { SUPABASE_FUNCTIONS_URL } from '../lib/supabase';
import { PerchLogo } from '../components/ui/PerchLogo';

/**
 * Share page for rebooked bookings.
 * Route: /s/:bookingId
 *
 * This page exists to serve OG meta tags for social previews.
 * When a user shares a rebook card, the share URL points here.
 * Social crawlers (iMessage, WhatsApp, Twitter) read the meta tags
 * and render the OG image in the link preview.
 *
 * For human visitors, we redirect to the main app after a brief moment.
 * The OG image is generated on-demand by the og-image edge function.
 */
export function SharePage() {
  const { bookingId } = useParams<{ bookingId: string }>();

  // Set OG meta tags dynamically
  useEffect(() => {
    if (!bookingId) return;

    const ogImageUrl = `${SUPABASE_FUNCTIONS_URL}/og-image?id=${bookingId}`;

    // Update meta tags for crawlers
    setMetaTag('og:image', ogImageUrl);
    setMetaTag('og:image:width', '1200');
    setMetaTag('og:image:height', '630');
    setMetaTag('og:image:type', 'image/svg+xml');
    setMetaTag('og:title', 'Perch saved me on this flight');
    setMetaTag('og:description', 'Perch watches flight prices after you book. When prices drop, they handle the rebook.');
    setMetaTag('og:url', window.location.href);
    setMetaTag('twitter:card', 'summary_large_image');
    setMetaTag('twitter:image', ogImageUrl);

    return () => {
      // Clean up meta tags on unmount
      removeMetaTag('og:image');
      removeMetaTag('og:image:width');
      removeMetaTag('og:image:height');
      removeMetaTag('og:image:type');
      removeMetaTag('og:title');
      removeMetaTag('og:description');
      removeMetaTag('og:url');
      removeMetaTag('twitter:card');
      removeMetaTag('twitter:image');
    };
  }, [bookingId]);

  return (
    <div className="min-h-screen bg-stone-50 dark:bg-stone-950 flex items-center justify-center px-4">
      <div className="text-center max-w-sm">
        <div className="mx-auto mb-4 flex justify-center">
          <PerchLogo size="lg" showText={false} />
        </div>
        <h1 className="text-2xl font-serif text-stone-900 dark:text-stone-100 mb-2">
          Perch Saved on This Flight
        </h1>
        <p className="text-stone-500 dark:text-stone-400 mb-6">
          Perch watches flight prices after you book. When prices drop, we handle the rebook.
        </p>
        <a
          href="/"
          className="inline-block px-6 py-3 bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900 font-medium rounded-full hover:opacity-90 transition-[transform,opacity,box-shadow,background-color]"
        >
          Get Started
        </a>
      </div>
    </div>
  );
}

function setMetaTag(property: string, content: string) {
  let tag = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('property', property);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

function removeMetaTag(property: string) {
  const tag = document.querySelector(`meta[property="${property}"]`);
  tag?.remove();
}
